import { Alert, Box, Button, FormControl, FormHelperText, FormLabel, Link, Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { RouterLink } from '@/components/router/RouterLink';
import { useAbility } from '@/hooks/useAbility';
import { I18N_DE } from '@/i18n/de';
import type { CreateAdminLevelUserDto } from '@/modules/api';
import { Action, AdminLevelUserRole, AdminLevelUserStatus, Subject, UserStatus } from '@/modules/api';
import {
  backendApiAdminLevelsCreateAdminLevelUserMutation,
  backendApiAdminLevelsFindOneAdminLevelUserOptions,
  backendApiAdminLevelsRemoveAdminLevelUserMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { useAdminLevelSuspenseQuery } from '@/queries/useAdminLevelSuspenseQuery';

export const Route = createFileRoute('/verwaltung/$adminLevelId/benutzerinnen/$userId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

type FormData = Pick<CreateAdminLevelUserDto, 'role' | 'status'>;

function RouteComponent() {
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const { ability } = useAbility();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: adminLevelUser } = useQuery(
    backendApiAdminLevelsFindOneAdminLevelUserOptions({
      path: {
        adminLevelId: params.adminLevelId,
        userId: params.userId,
      },
    }),
  );

  const { data: adminLevel } = useAdminLevelSuspenseQuery();

  const { mutateAsync } = useMutation({
    ...backendApiAdminLevelsCreateAdminLevelUserMutation(),
    onSuccess: async () => navigate({ to: '../..' }),
  });

  const { mutateAsync: mutateAsyncRemoveAdminLevelUser } = useMutation({
    ...backendApiAdminLevelsRemoveAdminLevelUserMutation(),
    onSuccess: async () => {
      navigate({ to: '../..' });
    },
  });

  const { control, handleSubmit, watch } = useForm<FormData>();

  const adminLevelHasNoManager =
    adminLevel &&
    !adminLevel.users?.some(
      ({ user, ...adminLevelUserItem }) =>
        adminLevelUserItem.status === AdminLevelUserStatus.ACTIVE &&
        adminLevelUserItem.role === AdminLevelUserRole.MANAGER &&
        user?.status === UserStatus.ACTIVE,
    );

  if (!adminLevelUser) {
    return null;
  }

  const userName = `${adminLevelUser.user?.firstName} ${adminLevelUser.user?.lastName}`;

  return (
    <Stack
      component="form"
      gap={2}
      onSubmit={handleSubmit(async (formData) => {
        await mutateAsync({
          path: {
            adminLevelId: params.adminLevelId,
            userId: params.userId,
          },
          body: formData,
        });
      })}
    >
      <Typography level="h3">Zugeordnete Verwaltungsebene</Typography>

      {adminLevelUser?.user?.status === UserStatus.INACTIVE && (
        <Alert color="danger" sx={{ marginBottom: 2 }} variant="outlined">
          <Box>
            <Typography level="title-lg">Gesperrte*r Benutzer*in</Typography>
            <Typography>
              Diese*r Benutzer*in ist global deaktiviert und hat keinen Zugriff auf die Plattform.
              {ability?.cannot(Action.MANAGE, Subject.User) ? ' Bei Fragen wenden Sie sich an den Support.' : null}
              {/* TODO: Link Support?  */}
            </Typography>
          </Box>
        </Alert>
      )}

      {!adminLevel?.active && (
        <Alert color="danger" sx={{ marginBottom: 2 }} variant="outlined">
          <Box>
            <Typography level="title-lg">Inaktive Verwaltungsebene</Typography>
            <Typography>
              Die Verwaltungsebene{' '}
              <RouterLink
                to="/verwaltung/$adminLevelId"
                params={{
                  adminLevelId: adminLevel.id,
                }}
                sx={{
                  fontSize: 'inherit',
                  color: 'inherit',
                  textDecorationColor: 'inherit',
                }}
              >
                {adminLevel?.name} ({I18N_DE.AdminLevelType[adminLevel.type]})
              </RouterLink>{' '}
              ist inaktiv. Die Aktivierung der Verwaltungsebene ist erforderlich um Benutzer*innen Zugriff auf diese zu
              gewähren.
            </Typography>
          </Box>
        </Alert>
      )}

      {adminLevelUser.user && adminLevelUser.adminLevel ? (
        <Typography>
          {ability?.can(Action.MANAGE, Subject.User) ? (
            <RouterLink params={{ userId: adminLevelUser.userId }} to="/verwaltung/benutzerinnen/$userId">
              {userName}
            </RouterLink>
          ) : (
            userName
          )}
          {` ist der Verwaltungsebene `}
          <RouterLink params={{ adminLevelId: adminLevelUser.adminLevelId }} to="/verwaltung/$adminLevelId">
            {`${adminLevelUser.adminLevel?.name} (${I18N_DE.AdminLevelType[adminLevelUser.adminLevel?.type]})`}
          </RouterLink>
          {' zugeordnet.'}
        </Typography>
      ) : null}

      <FormControl error={adminLevelHasNoManager}>
        <FormLabel>Rolle</FormLabel>
        <Controller
          control={control}
          defaultValue={adminLevelUser.role}
          name="role"
          render={({ field }) => (
            <RadioWithDescriptionGroup
              {...field}
              options={Object.values(AdminLevelUserRole)}
              optionLabel={getI18nRadioLabel({
                i18nTitle: I18N_DE.AdminLevelUserRole,
                i18nDescription: I18N_DE.AdminLevelUserRoleDescription,
              })}
            />
          )}
        />

        {adminLevelHasNoManager && !adminLevel.managedByParent ? (
          <FormHelperText sx={{ color: 'danger' }}>
            Die Verwaltungsebene{' '}
            <Link
              component={RouterLink}
              to={`/verwaltung/${adminLevel.id}`}
              sx={{
                fontSize: 'inherit',
                color: 'inherit',
                textDecorationColor: 'inherit',
              }}
            >
              {adminLevel?.name} ({I18N_DE.AdminLevelType[adminLevel.type]})
            </Link>{' '}
            hat aktuell keine*n aktive*n Manager*in.
          </FormHelperText>
        ) : null}
      </FormControl>

      <FormControl>
        <FormLabel>Status</FormLabel>
        <Controller
          control={control}
          defaultValue={adminLevelUser.status}
          name="status"
          render={({ field }) => (
            <RadioWithDescriptionGroup
              {...field}
              options={Object.values(AdminLevelUserStatus)}
              optionLabel={getI18nRadioLabel({
                i18nTitle: I18N_DE.AdminLevelUserStatus,
                i18nDescription: I18N_DE.AdminLevelUserStatusDescription,
              })}
            />
          )}
        />
      </FormControl>

      <Stack direction="row" gap={2} marginTop={4}>
        <Button color="danger" onClick={() => setConfirmOpen(true)} sx={{ marginRight: 'auto' }}>
          Zuordnung aufheben
        </Button>
        <ConfirmationModal
          content="Sind Sie sich sicher, dass die Sie die Zuordnung aufheben wollen?"
          onAbort={() => setConfirmOpen(false)}
          open={confirmOpen}
          title="Zuordnung aufheben"
          onConfirm={async () => {
            setConfirmOpen(false);
            await mutateAsyncRemoveAdminLevelUser({
              path: {
                adminLevelId: params.adminLevelId,
                userId: params.userId,
              },
            });
          }}
        />

        <Button
          disabled={watch('role') === adminLevelUser.role && watch('status') === adminLevelUser.status}
          type="submit"
        >
          Speichern
        </Button>
      </Stack>
    </Stack>
  );
}
